/**
 * Tenant subscription billing tab.
 *
 * Reads the tenant's current plan/renewal, lists live plans from
 * `/api/subscription-plans`, and drives Stripe Checkout via `/api/subscribe/checkout`.
 * After a successful checkout redirect (`?upgraded=1&session_id=…`), polls
 * `/api/subscribe/session/{id}` briefly to confirm the webhook fired.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  BadgeCheck, Calendar, CheckCircle2, CreditCard, ExternalLink, Loader2, RocketIcon, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

function PlanCard({ plan, current, billingCycle, onUpgrade, busy }) {
  const isCurrent = current === plan.code;
  const isFree = plan.code === "free";
  const price = billingCycle === "yearly"
    ? plan.price_yearly ?? (Number(plan.price_monthly || 0) * 10)
    : plan.price_monthly;

  return (
    <div
      className={[
        "rounded-2xl border p-5 transition",
        isCurrent
          ? "border-indigo-500 bg-indigo-50/40 ring-2 ring-indigo-100"
          : "border-slate-200 bg-white hover:shadow-md",
      ].join(" ")}
      data-testid={`billing-plan-${plan.code}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-heading text-xl">{plan.name}</div>
          {plan.description && <div className="mt-0.5 text-xs text-slate-500">{plan.description}</div>}
        </div>
        {isCurrent && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
            Current
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-3xl font-bold tabular-nums text-slate-900">
          {isFree ? "0" : price}
        </span>
        <span className="text-sm text-slate-500">
          {isFree ? "· free forever" : `${plan.currency} / ${billingCycle === "yearly" ? "yr" : "mo"}`}
        </span>
      </div>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
        {(plan.features || []).slice(0, 6).map((f) => (
          <li key={f.key} className="flex gap-1.5">
            {f.included ? (
              <BadgeCheck className="h-4 w-4 text-indigo-600 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-slate-300 shrink-0" />
            )}
            <span className={f.included ? "" : "text-slate-400 line-through"}>{f.label}</span>
          </li>
        ))}
      </ul>
      {!isFree && !isCurrent && (
        <Button
          onClick={() => onUpgrade(plan.code)}
          disabled={busy}
          data-testid={`billing-upgrade-${plan.code}`}
          className="mt-5 w-full bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              <CreditCard className="me-1 h-4 w-4" /> Upgrade to {plan.name}
            </>
          )}
        </Button>
      )}
      {isFree && !isCurrent && (
        <div className="mt-5 text-xs text-slate-500">
          Contact us if you&apos;d like to switch back to Free.
        </div>
      )}
      {isCurrent && (
        <div className="mt-5 text-xs text-indigo-700">You&apos;re on this plan.</div>
      )}
    </div>
  );
}

export default function BillingTab() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [busyCode, setBusyCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription-plans");
      setPlans(data.items || []);
    } catch (e) {
      // ignore — UI just shows nothing
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Post-Checkout confirmation: read ?upgraded=1&session_id=…
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("cancelled") === "1") {
      toast.info("Checkout cancelled.");
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    if (p.get("upgraded") !== "1") return;
    const sid = p.get("session_id");
    if (!sid) return;
    setConfirming(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/subscribe/session/${sid}`);
        if (!data.pending) {
          setLastPayment(data.payment);
          setConfirming(false);
          toast.success("Upgrade confirmed! Your new plan is active.");
          window.history.replaceState(null, "", window.location.pathname + "#billing");
          return;
        }
      } catch (_) { /* ignore */ }
      if (attempts >= 12) {
        setConfirming(false);
        toast.warning("Payment recorded but webhook is still processing. Refresh in a moment.");
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
  }, []);

  async function upgrade(planCode) {
    setBusyCode(planCode);
    try {
      const { data } = await api.post("/subscribe/checkout", {
        plan_code: planCode,
        billing_cycle: billingCycle,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "stripe_not_configured") {
        toast.error("Stripe billing isn't configured yet. Contact support.");
      } else {
        toast.error(d?.message || "Failed to start checkout.");
      }
      setBusyCode("");
    }
  }

  const currentCode = company?.subscription_plan || "free";
  const planEndsAt = company?.plan_ends_at;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="billing-current">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Current subscription
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="font-heading text-2xl text-slate-900" data-testid="billing-current-plan">
                {currentCode.toUpperCase()}
              </div>
              {company?.billing_cycle && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  {company.billing_cycle}
                </span>
              )}
            </div>
            {planEndsAt && (
              <div className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                <Calendar className="h-3.5 w-3.5" />
                Renews / ends: <span className="font-medium text-slate-800">{new Date(planEndsAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          {confirming && (
            <div className="flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-700" data-testid="billing-confirming">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Confirming payment…
            </div>
          )}
          {lastPayment && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="billing-last-payment">
              <div className="flex items-center gap-1 font-semibold">
                <CheckCircle2 className="h-3.5 w-3.5" /> Paid {lastPayment.amount} {lastPayment.currency}
              </div>
              <div className="mt-0.5">Session: <code className="text-[10px]">{lastPayment.stripe_session_id.slice(0, 24)}…</code></div>
            </div>
          )}
        </div>
      </section>

      {/* Billing cycle toggle */}
      <div className="flex items-center gap-2" data-testid="billing-cycle-toggle">
        <span className="text-sm text-slate-600">Billing cycle:</span>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {["monthly", "yearly"].map((c) => (
            <button
              key={c}
              onClick={() => setBillingCycle(c)}
              data-testid={`billing-cycle-${c}`}
              className={[
                "rounded-md px-3 py-1 text-sm capitalize transition",
                billingCycle === c ? "bg-indigo-600 text-white" : "text-slate-700 hover:bg-slate-100",
              ].join(" ")}
            >
              {c}
              {c === "yearly" && <span className="ms-1 text-[10px] font-semibold uppercase text-emerald-500">2 mo free</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid gap-4 md:grid-cols-3" data-testid="billing-plans">
        {plans.map((plan) => (
          <PlanCard
            key={plan.code}
            plan={plan}
            current={currentCode}
            billingCycle={billingCycle}
            onUpgrade={upgrade}
            busy={busyCode === plan.code}
          />
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        <div className="mb-1 flex items-center gap-1 font-semibold text-slate-700">
          <RocketIcon className="h-3.5 w-3.5" /> Powered by Stripe
        </div>
        Card details go directly to Stripe (PCI-DSS certified). We never see or store them.
        <br />
        Test mode is active — use card <code className="font-mono">4242 4242 4242 4242</code>, any future expiry, any CVC.
      </div>
    </div>
  );
}
